import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Stack,
    Grid,
    Paper,
    Chip,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Tabs,
    Tab
} from '@mui/material';
import {
    Close,
    Image as ImageIcon,
    Label,
    Storage,
    Layers,
    CheckCircle,
    Error as ErrorIcon,
    HourglassEmpty,
    DataObject,
    Folder,
    Timeline
} from '@mui/icons-material';
import axiosDirectApi from 'utils/axiosDirectApi';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
        </div>
    );
}

const DatasetDetailsDialog = ({ open, onClose, datasetId }) => {
    const [dataset, setDataset] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);

    useEffect(() => {
        if (open && datasetId) {
            loadDatasetDetails();
        }
    }, [open, datasetId]);

    const loadDatasetDetails = async () => {
        try {
            setLoading(true);
            const response = await axiosDirectApi.get(`/cv/datasets/${datasetId}`);
            setDataset(response.data);
        } catch (error) {
            console.error('Failed to load dataset details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCurrentTab(0);
        onClose();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ready':
                return <CheckCircle sx={{ fontSize: 20 }} />;
            case 'error':
                return <ErrorIcon sx={{ fontSize: 20 }} />;
            default:
                return <HourglassEmpty sx={{ fontSize: 20 }} />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ready':
                return 'success';
            case 'error':
                return 'error';
            default:
                return 'warning';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('vi-VN');
    };

    const formatBytes = (bytes) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes === 0) return '0 Bytes';
        const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
        return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
    };

    if (!dataset && !loading) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '70vh' }
            }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            Chi tiết Dataset
                        </Typography>
                        {dataset && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {dataset.name}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ width: '100%', py: 4 }}>
                        <LinearProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                            Đang tải thông tin dataset...
                        </Typography>
                    </Box>
                ) : dataset ? (
                    <Box>
                        {/* Status Banner */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: `${getStatusColor(dataset.status)}.lighter`,
                                border: '1px solid',
                                borderColor: `${getStatusColor(dataset.status)}.light`
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                {getStatusIcon(dataset.status)}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Trạng thái: {dataset.status}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {dataset.processing_stage || 'N/A'}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Chip
                                        label={dataset.format?.toUpperCase() || 'N/A'}
                                        color="primary"
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                    />
                                    <Chip label={dataset.dataset_type || 'N/A'} variant="outlined" size="small" />
                                </Stack>
                            </Stack>
                        </Paper>

                        {/* Tabs */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
                                <Tab icon={<DataObject />} iconPosition="start" label="Tổng quan" />
                                <Tab icon={<Layers />} iconPosition="start" label="Lakehouse Layers" />
                                <Tab icon={<Timeline />} iconPosition="start" label="Thống kê" />
                            </Tabs>
                        </Box>

                        {/* Tab: Overview */}
                        <TabPanel value={currentTab} index={0}>
                            <Stack spacing={3}>
                                {/* Basic Info */}
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                        Thông tin cơ bản
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '30%' }}>ID</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {dataset.id}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Tên</TableCell>
                                                <TableCell>{dataset.name}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Loại</TableCell>
                                                <TableCell>
                                                    <Chip label={dataset.dataset_type} size="small" variant="outlined" />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Format</TableCell>
                                                <TableCell>
                                                    <Chip label={dataset.format?.toUpperCase()} size="small" color="primary" />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Project</TableCell>
                                                <TableCell>{dataset.project_id || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Tenant ID</TableCell>
                                                <TableCell>{dataset.tenant_id || 'N/A'}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Box>

                                {/* Classes */}
                                {dataset.classes && dataset.classes.length > 0 && (
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                            Classes ({dataset.classes.length})
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {dataset.classes.map((cls, index) => (
                                                <Chip key={index} label={cls} size="small" color="secondary" sx={{ mb: 1 }} />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                {/* Timestamps */}
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                        Thời gian
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '30%' }}>Tạo lúc</TableCell>
                                                <TableCell>{formatDate(dataset.created_at)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Cập nhật lần cuối</TableCell>
                                                <TableCell>{formatDate(dataset.updated_at)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Stack>
                        </TabPanel>

                        {/* Tab: Lakehouse Layers */}
                        <TabPanel value={currentTab} index={1}>
                            <Stack spacing={2}>
                                {/* Bronze Layer */}
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Storage color="warning" sx={{ fontSize: 32 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Bronze Layer (Raw Data)
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.5 }}
                                            >
                                                {dataset.bronze_path || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>

                                {/* Silver Layer */}
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Layers color="info" sx={{ fontSize: 32 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Silver Layer (Cleaned & Organized)
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.5 }}
                                            >
                                                {dataset.silver_path || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>

                                {/* Gold Layer */}
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Folder color="success" sx={{ fontSize: 32 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Gold Layer (Training Ready)
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.5 }}
                                            >
                                                {dataset.gold_path || 'Chưa xử lý'}
                                            </Typography>
                                            {dataset.gold_path && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Chip label="train/" size="small" color="primary" variant="outlined" />
                                                        <Chip label="val/" size="small" color="primary" variant="outlined" />
                                                        <Chip label="test/" size="small" color="primary" variant="outlined" />
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </TabPanel>

                        {/* Tab: Statistics */}
                        <TabPanel value={currentTab} index={2}>
                            <Stack spacing={3}>
                                {/* Image/Label Stats */}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                bgcolor: 'primary.lighter',
                                                border: '1px solid',
                                                borderColor: 'primary.light',
                                                textAlign: 'center'
                                            }}
                                        >
                                            <ImageIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                {dataset.total_images || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Tổng số Images
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                bgcolor: 'secondary.lighter',
                                                border: '1px solid',
                                                borderColor: 'secondary.light',
                                                textAlign: 'center'
                                            }}
                                        >
                                            <Label sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                                            <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                                                {dataset.total_labels || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Tổng số Labels
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* Split Statistics (if available) */}
                                {dataset.gold_path && dataset.split_stats && (
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                            Phân chia Train/Val/Test
                                        </Typography>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500 }}>Train</TableCell>
                                                    <TableCell align="right">{dataset.split_stats.train?.images || 0} images</TableCell>
                                                    <TableCell align="right">{dataset.split_stats.train?.labels || 0} labels</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500 }}>Validation</TableCell>
                                                    <TableCell align="right">{dataset.split_stats.val?.images || 0} images</TableCell>
                                                    <TableCell align="right">{dataset.split_stats.val?.labels || 0} labels</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500 }}>Test</TableCell>
                                                    <TableCell align="right">{dataset.split_stats.test?.images || 0} images</TableCell>
                                                    <TableCell align="right">{dataset.split_stats.test?.labels || 0} labels</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}

                                {/* Additional Stats */}
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                        Thông tin khác
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '40%' }}>Số lượng Classes</TableCell>
                                                <TableCell>{dataset.classes?.length || 0}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Kích thước (ước tính)</TableCell>
                                                <TableCell>{formatBytes(dataset.total_size)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Stack>
                        </TabPanel>
                    </Box>
                ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        Không tìm thấy thông tin dataset
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                    Đóng
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DatasetDetailsDialog;
